/* global define,workspace */

/**
 * @file filterViews.js
 * @desc Filter view objects: This file contains view objects that render filter
 * view for use in data box configs.
 * @author Geoffrey Pursell
 */

define('cardboard/filterViews',[
    // libraries
    "jquery", "underscore", "underscore.string", "backbone", "hogan", "typeahead", "rsvp", "handlebars",

    // app modules
    "commonDataSources", "cardboard/baseViews", "cardboard/pluginHelper", "cardboard/util",

    // templates
    "text!templates/filterMenuOptions.html!strip", "text!templates/typeahead_suggestion.html!strip", "text!templates/typeahead_district_suggestion.html!strip", "text!templates/filters.html!strip", "text!templates/democats.html!strip", "text!templates/democatsMulti.html!strip"
],
    function ($, _, _s, Backbone, Hogan, Typeahead, RSVP, Handlebars,
        CommonDataSources, Views, PluginHelper, Util,
        filterMenuOptionsTemplate, suggestionTemplate, districtSuggestionTemplate, filtersTemplate, demoCatsTemplate, demoCatsMultiTemplate) {

        console.log("MODULE: cardboard/filterViews");

        /**
         * @exports cardboard/filterViews
         * @desc databox filter view objects
         */
        var FilterViews = {},
            Promise = RSVP.Promise;

        if (typeof suggestionTemplate === "string") {
            suggestionTemplate = Hogan.compile(suggestionTemplate);
        }
        if (typeof districtSuggestionTemplate === "string") {
            districtSuggestionTemplate = Hogan.compile(districtSuggestionTemplate);
        }
        if (typeof demoCatsTemplate === "string") {
            demoCatsTemplate = Hogan.compile(demoCatsTemplate);
        }
        if (typeof demoCatsMultiTemplate === "string") {
            demoCatsMultiTemplate = Hogan.compile(demoCatsMultiTemplate);
        }

        var suggestionSchoolHeaderTemplate;

        // If the application is ECLDS replace the Schools header with Districts since this is a District Finder.
        if (typeof window.appECLDS === 'undefined' || window.appECLDS === null) {
            suggestionSchoolHeaderTemplate = Hogan.compile("<div class='header'>Schools</div>");
        }
        else {
            suggestionSchoolHeaderTemplate = Hogan.compile("<div class='header'>School Districts</div>");
        }

        var suggestionDistrictHeaderTemplate = Hogan.compile("<div class='header'>Districts</div>"),
            suggestionCountyHeaderTemplate = Hogan.compile("<div class='header'>School Counties</div>"),
            suggestionStateHeaderTemplate = Hogan.compile("<div class='header'>State</div>"),
            suggestionProgramHeaderTemplate = Hogan.compile("<div class='header'>Programs</div>"),
            suggestionConsortiumHeaderTemplate = Hogan.compile("<div class='header'>Consortium</div>"),
            suggestionCharterAuthorizerHeaderTemplate = Hogan.compile("<div class='header'>Charter School Authorizers</div>");

        /**
         * @class module:filterViews.BaseFilterView
         * @augments Views.BaseView
         * @classdesc This if for methods common to all filter views. It was created
         * to provide the same destructor function to all of them.
         */
        FilterViews.BaseFilterView = Views.BaseView.extend( /** @lends module:filterViews.BaseFilterView.prototype */ {
            destroy: function () {
                this.undelegateEvents();
                this.stopListening();
                // in this case, we may not want to remove the element, you know? It
                // wasn't put there by us, so it shouldn't be deleted by us.
            },
            hide: function () {
                this.$el.hide();
            },
            show: function () {
                this.$el.show();
            }
        });

        /**
         * @class module:filterViews.FilterMenusView
         * @augments Views.BaseView
         * @classdesc This renders a single pane inside a data box. It's
         * responsible for rendering itself, and probably some
         * controller-ish duties, for simplicity
         */
        FilterViews.FilterMenusView = FilterViews.BaseFilterView.extend( /** @lends module:filterViews.FilterMenusView.prototype */ {

            /** declarative events. empty for now
             * @type Object */
            events: {
                "change select.filter": "onFilterChanged"
            },

            globalEvents: {
                "beforeWorkspaceStateChange": "onBeforeWorkspaceStateChange"
            },

            loaded: false,

            initialize: function (options) {
                this.options = options;

                this.isCat = false;

                // Do the right thing -- there might not even be a model
                // attached. What if this report has no filters other
                // than the org finder? I bet you hadn't thought of that. I
                // sure hadn't.
                if (this.model) {
                    if (this.model.isLoaded()) {
                        this.render();
                    } else {
                        this.showProgress(".content");
                    }
                    // bind to model's change event
                    // THIS IS CAUSING A DOUBLE-RENDER.
                    this.listenTo(this.model, "change", this.render);
                } else {
                    // no data source for this, just render
                    this.render();
                }
                this.attachGlobalEvents();
            },

            render: function () {
                this.unmaskForLoading();

                if (this.model) {
                    // ferret out the initial state of the filter
                    // menus. this is presently dependent on the
                    // WebFOCUS-style decision-tree filter data structure
                    var hiddenFieldValues = this.captureHiddenFieldValues(),
                        dataView = {
                            menus: this.menuData = this.model.getDefaultMenus()
                        };

                    _(dataView.menus).each(function (menu) {
                        if (!menu.type || menu.type === "menu") {
                            menu.isMenu = true;
                        } else if (menu.type === "hidden") {
                            menu.isHidden = true;
                        }
                    });

                    // actually create the menus and attach them to the DOM
                    this.$el.html(Hogan.compile(filtersTemplate).render(dataView, {
                        options: filterMenuOptionsTemplate
                    }));

                    // create the jqMobile select menus
                    this.$el.find("select").selectmenu();

                    // is this is MMR or some other plugin that takes separate
                    // parameters rather than a single org ID, we have to ask the view
                    // split the orgID apart into its consituent parts and put them in
                    // hidden fields.
                    this.options.parentView.dataBoxView.splitOrgID();

                    // this.restoreHiddenFieldValues(hiddenFieldValues);
                    this.trigger("filterMenusRendered");
                } else {
                    // we want an empty filters box.
                    this.$el.html("");
                }

                // let people know we're done here
                this.loaded = true;
            },

            /** replace the contents and selections (whenever possible) of the
             * filter menus when one is changed, or when the org changes */
            updateMenus: function (selectionStateToRestore) {
                try {
                    var currentSelections = (selectionStateToRestore ||
                        this.$el.closest("form").serializeObject()),
                        $menus = this.$("select"),
                        menuObjects;

                    // if there are filters present in this report
                    if (this.model) {
                        menuObjects = this.menuData = this.model.getCurrentMenus(currentSelections);

                        $menus.each(function (index, menu) {

                            var $menu = $(menu),
                                menuFormName = $menu.attr("name"),
                                menuObject = _(menuObjects).where({
                                    paramName: menuFormName
                                }).first(),
                                previousSelectionValue = currentSelections[menuFormName],
                                defaultOptionValue = _(menuObject.options).first().code;

                            if (previousSelectionValue !== undefined) {
                                previousSelectionValue = previousSelectionValue.replace(/[+]/g, ' ');
                            }

                            /*      if (menuObject.paramName == "graduationYearRate") {

                                     if (this.$el.closest("form").find('#box_year').val() == "2014")
                                         menuObject.options = [{
                                             'code': '4',
                                             'displayName': '4-Year Rate',
                                             'filterCode': 'graduationYearRate',
                                             'filterLabel': 'Cohort',
                                             'filterSubLabel': null,
                                             'filterUrl': 'graduationYearRate',
                                             'isDefault': true,
                                             'sort': 1,
                                             'url': '4'
                                         }, {
                                             'code': '7',
                                             'displayName': '7-Year Rate',
                                             'filterCode': 'graduationYearRate',
                                             'filterLabel': 'Cohort',
                                             'filterSubLabel': null,
                                             'filterUrl': 'graduationYearRate',
                                             'isDefault': false,
                                             'sort': 4,
                                             'url': '7'
                                         }];
                                 } */
                            // re-render the options list and slam it in

                            $menu.html(Hogan.compile(filterMenuOptionsTemplate).render(menuObject));

                            // re-select the previously selected option, if
                            // possible, otherwise select the default
                            if (_(menuObject.options).where({
                                code: previousSelectionValue
                            }).first()) {
                                $menu.val(previousSelectionValue);
                            } else {
                                $menu.val(defaultOptionValue);
                            }

                            this.trigger("filterChangedAutomatically", {
                                menuElement: $menu[0],
                                menuData: menuObject,
                                selection: _.find(menuObject.options, {
                                    code: $($menu[0]).val()
                                })
                            });

                            // refresh the custom select rendering

                            $menu.selectmenu("refresh");
                          
                        }.bind(this));
                    }
                } catch (error) {
                    console.log("couldn't set menu selections.");
                    //    console.log(error);
                }
            },
            /** returns a hash representing all the hidden fields we find in the
             * filters' neighborhood. these get put there by hidden field filter
             * patches, and are needed especially my MMR and proficiency.
             * @returns {Object} key/value pairs from the hidden fields */
            captureHiddenFieldValues: function () {
                return _(this.$("input[type=hidden]"))
                    .map(function (field) {
                        return [$(field).attr("name"), $(field).val()];
                    })
                    .object().value();
            },
            /** restores values to the hidden fields from a values hash */
            restoreHiddenFieldValues: function (valuesHash) {
                _(valuesHash).each(function (value, name) {
                    this.$("input[name=" + name + "]").val(value);
                }.bind(this));
            },

            //
            // EVENT HANDLERS
            //

            // handles a DOM event
            triggerFilterChangedEvent: function (menuElement) {
                var menuData = _.find(this.menuData, {
                    paramName: menuElement.name
                });

                // fire a backbone event
                this.trigger("filterChanged", {
                    menuElement: menuElement,
                    menuData: menuData,
                    selection: _.find(menuData.options, {
                        code: $(menuElement).val()
                    })
                });

                /*  //Focus the next available thing.
                  var $db = this.$el.closest(".dataBox");
                  if ($db.find('.filters2').children().length > 0) {
                      $db.find('.filters2 input, .filters2 select').eq(0).focus();
                  } 
                  else if ($db.find('.filters3').children().length > 0) {
                      $db.find('.filters3 input, .filters3 select').eq(0).focus();
                  } 
                  else if ($db.find('.demoCats').children().length > 0) {
                      $db.find('.demoCats input, .demoCats select').eq(0).focus();
                  } else {
                      $db.find('.handle').eq(0).focus();
                  }*/
            },
            onFilterChanged: function (event /*, info*/) {
                this.triggerFilterChangedEvent(event.target);
            },
            onBeforeWorkspaceStateChange: function () {
                // prevents the view from spuriously redrawing itself during a
                // report change
                this.stopListening();
            }
        });

        /**
         * @class module:cardboard/filterViews.OrgFinderView
         * @augments Views.BaseView
         * @classdesc This just handles view duties for the org finder,
         * which sits in the config drawer
         */

        FilterViews.OrgFinderView = FilterViews.BaseFilterView.extend( /** @lends module:cardboard/filterViews.OrgFinderView.prototype */ {

            /** declarative events. empty for now
             * @type Object */
            events: {
                "typeahead:selected input": "onTypeaheadChanged", // jquery custom event
                'typeahead:cursorchanged': 'onTypeaheadCursorMoved',
                "click .orgNameClearButton": "onTypeaheadClearButtonPressed",
                "click .orgName": "onOrgNameClicked",
                "mousedown .orgName": "onOrgNameMouseDown",
                "keydown .orgName": "onOrgNameKeyPressed",
                "focus .orgName": "onOrgNameFocusIn",
                "blur .orgName": "onOrgNameFocusOut",
                "click .mapLink": "onMapButtonClicked",
                "click .resetLink": "onResetButtonClicked"
            },
            globalEvents: {
                "beforeHamburgerDrawerOpen": "onBeforeHamburgerDrawerOpen"
            },

            loaded: false,

            initialize: function (options) {
                this.options = options;

                // for convenience
                this.$orgName = this.$("input.orgName");
                this.isCat = false;

                // this checking may be unnecessary, since this object
                // doesn't currently get created until we know the org
                // list has loaded.
                if (!this.model.isLoaded()) {
                    this.showProgress(".content");
                }
            },

            destroy: function () {
                this.$orgName.typeahead("destroy");
                this.stopListening();
                this.undelegateEvents();
                this.remove();
            },

            /** "render" the view -- actually, set up the machinery, the
             * element's already there */
            render: function () {
                var orgs = this.getOrgList();

                if (!orgs) {
                    var errorMessage = "OrgFinderView error: missing org list.";
                    console.log(errorMessage);
                    throw errorMessage;
                }
                if (this.alreadyRendered()) {
                    this.$orgName.typeahead("destroy");
                }

                // INIT THE ORG FINDER, if we haven't already
                this.$orgName.typeahead({
                    //options
                    autoselect: true,
                    highlight: true,
                    hint: true,
                    accessibleStatus: Handlebars.compile('{{#unless isEmpty}}{{count}} suggestions available.' +
                        '{{#if withHint}}Top suggestion {{hint}} can be chosen by right arrow or tab key.{{/if}}{{/unless}}')
                }, {
                    name: "state",
                    source: (CommonDataSources.Derivative.stateBloodhound || orgs.stateBloodhound).ttAdapter(),
                    displayKey: "name",
                    templates: {
                        suggestion: districtSuggestionTemplate.render.bind(districtSuggestionTemplate),
                        header: suggestionStateHeaderTemplate.render.bind(suggestionStateHeaderTemplate)
                    }
                }, {
                    name: "schools",
                    source: (CommonDataSources.Derivative.schoolsBloodhound || orgs.schoolsBloodhound).ttAdapter(),
                    displayKey: "name",
                    templates: {
                        suggestion: suggestionTemplate.render.bind(suggestionTemplate),
                        header: suggestionSchoolHeaderTemplate.render.bind(suggestionSchoolHeaderTemplate)
                    }
                }, {
                    name: "districts",
                    source: (CommonDataSources.Derivative.districtsBloodhound || orgs.districtsBloodhound).ttAdapter(),
                    displayKey: "name",
                    templates: {
                        suggestion: districtSuggestionTemplate.render.bind(districtSuggestionTemplate),
                        header: suggestionDistrictHeaderTemplate.render.bind(suggestionDistrictHeaderTemplate)
                    }
                }, {
                    name: "county",
                    source: (CommonDataSources.Derivative.countiesBloodhound || orgs.countiesBloodhound).ttAdapter(),
                    displayKey: "name",
                    templates: {
                        suggestion: districtSuggestionTemplate.render.bind(districtSuggestionTemplate),
                        header: suggestionCountyHeaderTemplate.render.bind(suggestionCountyHeaderTemplate)
                    }
                }, {
                    name: "charterAuthorizers",
                    source: (CommonDataSources.Derivative.charterAuthorizersBloodhound || orgs.charterAuthorizersBloodhound).ttAdapter(),
                    displayKey: "name",
                    templates: {
                        suggestion: districtSuggestionTemplate.render.bind(districtSuggestionTemplate),
                        header: suggestionCharterAuthorizerHeaderTemplate.render.bind(suggestionCharterAuthorizerHeaderTemplate)
                    }
                },
                    {
                        name: "program",
                        source: (CommonDataSources.Derivative.programsBloodhound || orgs.programsBloodhound).ttAdapter(),
                        displayKey: "name",
                        templates: {
                            //suggestion: districtSuggestionTemplate.render.bind(districtSuggestionTemplate),
                            suggestion: suggestionTemplate.render.bind(suggestionTemplate),
                            header: suggestionProgramHeaderTemplate.render.bind(suggestionProgramHeaderTemplate)
                        }
                    },
                    {
                        name: "consortium",
                        source: (CommonDataSources.Derivative.consortiumBloodhound || orgs.consortiumBloodhound).ttAdapter(),
                        displayKey: "name",
                        templates: {
                            suggestion: districtSuggestionTemplate.render.bind(districtSuggestionTemplate),
                            header: suggestionConsortiumHeaderTemplate.render.bind(suggestionConsortiumHeaderTemplate)
                        }
                    }

                );

                //We don't have access to the template for twitter dropdown, but we need accesibility changes
                this.$ttMenu = this.$el.find('.tt-dropdown-menu');
                this.$ttMenu.attr({
                    "role": "listbox",
                    "id": "dropdown-listbox"
                });

                // bookkeeping for rendering status
                this.markRendered();

                // jquerify the buttons row
                this.$(".row.buttons").trigger("create");

                // let people know we're done here
                this.loaded = true;

                this.trigger("orgFinderRendered", this);
            },

            // ACTIONS

            /** Set the selected organization. This will not set the text contents
             * of the typeahead for you, but it will fetch data and re-render the
             * whole data box.
             * @param {object} datum - an org datum object
             * @param {string} source - a string describing the source of the request ("map"
             * or "typehead")*/
            setOrg: function (datum, source) {

                /*  if (datum.orgID !== this.getOrgID() || datum.groupType !== this.getOrgGroupType()) {*/
                this.chosenOrg = datum;

                if (source == "map") {
                    datum.groupType = datum.type;
                }
                // set some hidden values
                this.setHiddenOrgFields(datum);
                this.splitOrgID();

                this.$orgName.blur();

                this.trigger("orgChanged", {
                    source: source,
                    orgID: datum.orgID,
                    datum: datum
                });

                //        }
            },

            /** set the org, with a "synthetic" datum given to us by the map
             * @param {object} datum - an org datum object */
            setOrgFromMap: function (datum) {
                // we have to manually set the typeahead contents
                this.setQuery(datum.name);

                this.setOrg(datum, "map");
            },

            setFY: function (datum) {

            },

            /** this DOES set the contents of the typeahead for you.
             * @param {string} value - the new contents of the typeahead */
            setQuery: function (value) {
                //   if ( (location.hash.indexOf("scholarships") > -1))
                // {
                this.$orgName.typeahead("val", value);
                //console.log(value);
                //} 
            },

            /** this sets the ghosted "hint" that appears behind the typeahead.
                     @param {string} value - the value to set the hint to */
            setHint: function (value) {
                // this is hackish, but it works reliably. We have to do this
                // because otherwise, as soon as the typeahead gets focus, it blanks
                // out its hint and overwrites whatever we put there.
                //       _.defer(function () {
                //        this.$orgName.siblings(".tt-hint").val(value);
                //      }.bind(this));
            },

            /** makes the typeahead completion list go away. */
            closeSuggestions: function () {
                this.$orgName.siblings(".tt-dropdown-menu").hide();
            },

            /** clears the typehead and sets the hint to the "invitation" state. */
            clearField: function () {
                this.setQuery("");
                this.setHint("Type here to search...");
            },

            /** sets the typeahead contents to match whatever is currently rendered
             * by the databox, which is Statewide by default or is otherwise the
             * last selected org. */
            restoreQuery: function () {
                var org = this.getOrg();
                var str2 = location.host;
                if ((str2.indexOf("eclds") >= 0) || (str2.indexOf("sleds") >= 0)) {
                    _.defer(this.clearField.bind(this));
                    //this.setQuery("All"); 
                } else if (org) {
                    this.setQuery(org.name);
                }
            },

            /** selects the contents of the typeahead. unused, currently. */
            selectAll: function () {
                this.$orgName.select();
            },

            /** selects the top match and closes the suggestions. */
            chooseFirstSuggestion: function () {
                this.$(".tt-suggestions .tt-suggestion").first().click();
            },

            /** this sets the hidden orgID field, which is needed for form
             * serialization purposes. You probably don't need to call this; why are
             * you here?
             * @param {object} datum - an org datum
             */
            setHiddenOrgFields: function (datum) {
                this.$("input[name=orgId]").val(datum.orgID);
                this.$("input[name=groupType]").val(datum.groupType);
            },

            /** clear and focus the typeahead. be aware that setting focus is
                    problematic on iOS. */
            clearAndFocusTypeahead: function () {
                this.clearField();
                this.$orgName.focus();
            },
            restoreOriginalScroll: function () {
                if (this.hasCausedAutoScroll()) {
                    var originalPos = this.beforeAutoScroll;
                    delete this.beforeAutoScroll;
                    //   return Util.scrollToPixel(originalPos);
                }
                return Promise.resolve();
            },

            /** called when the org changes; this splits up the org ID into its
             * components so the school and district ID and type can be put into
             * hidden "filter" fields to be passed through as parameters to any data
             * source that needs them.
             * NOTE: why is this here, instead of on the org finder object? */
            splitOrgID: function () {
                var orgIDValue = this.$("input[name=orgId]").val();
                var org = new Util.Data.OrgID({
                    wfOrgID: orgIDValue
                });
                var $db = this.$el.closest(".dataBox");

                $db.find("input[name=districtNumber]").val(org.districtNumber);
                $db.find("input[name=districtType]").val(org.districtType);
                $db.find("input[name=schoolNumber]").val(org.schoolNumber);
            },

            // INFO

            /** @returns {object} the most recently selected org */
            getOrg: function () {
                return this.chosenOrg;
            },
            /** @returns {string} the most recently selected org's orgID */
            getOrgID: function () {
                var org = this.getOrg();
                if (org) {
                    return org.orgID;
                }
                return null;
            },
            /** @returns {string} the most recently selected org's groupType */
            getOrgGroupType: function () {
                var org = this.getOrg();
                if (org) {
                    return org.groupType;
                }
                return null;
            },
            /** @returns {string} the most recently selected org's name */
            getOrgName: function () {
                var org = this.getOrg();
                if (org) {
                    return org.name;
                }
                return null;
            },
            /** digs out the org list from the global data sources (if there is one)
             * or way back in the object tree if we're using a filter-style org
             * list. tightly coupled, but it works.
             * @returns {string} */
            getOrgList: function () {
                return (CommonDataSources.entities ||
                    this.options.parentView.dataBoxView.controller.filterSources.orgs);
            },

            /** has the typeahead really changed?
             * @returns {boolean} whether or not the typeahead field's contents
             * match the name of the most recently selected org */
            typeaheadMatchesRenderedOrgName: function () {
                return (this.$orgName.val() === this.getOrgName());
            },

            /** Whether a focus on the element has caused and auto-scroll or not.
             * @returns {Number | Boolean} if the filter drawer has caused an
             * auto-scroll, the original position in pixels; otherwise, false */
            hasCausedAutoScroll: function () {
                return _.has(this, "beforeAutoScroll");
            },

            // EVENTS

            onOrgListsReady: function () {
                this.render();
            },
            onTypeaheadCursorMoved: function (evt, datum, source) {
                this.$orgName.attr('aria-activedescendant', 'typeahead_item_' + datum.orgID)

            },
            onTypeaheadChanged: function (event, datum) {
                var that = this;
                var str2 = location.href;

                if ((str2.indexOf("eclds") > -1) || (str2.indexOf("SLEDS") > -1) || ((str2.indexOf("sleds") > -1) && (location.hash.indexOf("abeEducationOutcomes") < 0))) {

                    var $db = this.$el.closest(".dataBox");
                    //Focus the next element
                    if (($db.find('select#box_ECO_DEV_REGION').length > 0) || ($db.find('select#box_ECODEVREGION').length > 0)) {
                         //   console.log($(this.el.nextSibling.nextSibling.nextSibling.children[0].children[1].children[0].children[0].children[1]));
                        $(this.el.nextSibling.nextSibling.nextSibling.children[0].children[1].children[0].children[0].children[1]).prop('selectedIndex', 0);
                       // console.log($db.find('select#box_ECO_DEV_REGION'));
                        $db.find('select#box_ECO_DEV_REGION').value = "FOC_NONE";
                        $db.find('select#box_ECO_DEV_REGION').selectmenu("refresh");
                        $db.find('select#box_ECODEVREGION').value = "FOC_NONE";
                        $db.find('select#box_ECODEVREGION').selectmenu("refresh");
                    }
                    if (location.hash.indexOf("outStateCollegeStudentEnrollment") > -1) {
                        $(this.el.nextSibling.nextSibling.nextSibling.children[1].children[1].children[0].children[0].children[1]).prop('selectedIndex', 0);
                        $(this.el.nextSibling.nextSibling.nextSibling.children[2].children[1].children[0].children[0].children[1]).prop('selectedIndex', 0);

                        $db.find('select#box_INSTITUTION_TYPE').value = "FOC_NONE";
                        $db.find('select#box_INSTITUTION_TYPE').selectmenu("refresh");
                        $db.find('select#box_COLLEGE_SELECTIVITY').value = "FOC_NONE";
                        $db.find('select#box_COLLEGE_SELECTIVITY').selectmenu("refresh");
                        $db.find('select#box_COLLEGE_STATE').val("FOC_NONE");
                        $db.find('select#box_COLLEGE_STATE').selectmenu("refresh");
                    }
                }

                // if (datum.orgID !== this.getOrgID() || datum.groupType !== this.getOrgGroupType()) {
                if (this.hasCausedAutoScroll()) {
                    workspace.showFixedHeader()
                        .then(function () {
                            return that.restoreOriginalScroll();
                        })
                        .then(function () {
                            that.setOrg(datum, "typeahead");
                        })
                        .catch(Util.reportPromiseException);
                } else {
                    this.setOrg(datum, "typeahead");

                }
                //  }
            },
            onTypeaheadClearButtonPressed: function (event) {
                event.preventDefault();
                this.clearAndFocusTypeahead();
            },
            onOrgNameClicked: function ( /*event*/) {

                if (this.typeaheadMatchesRenderedOrgName()) {
                    this.clearAndFocusTypeahead();
                }

            },
            onOrgNameMouseDown: function (event) {
                // prevent the typeahead completion menu from flashing open for a
                // moment if you click on the typeahead while its value matches the
                // name of the currently rendered org
                if (this.typeaheadMatchesRenderedOrgName()) {
                    event.preventDefault();
                }
            },
            onOrgNameKeyPressed: function (event, datum) {
                if (event.keyCode === 27) { // ESC key
                    event.preventDefault();
                    if (!this.typeaheadMatchesRenderedOrgName()) {
                        event.stopPropagation();
                        this.restoreQuery();
                    } else {
                        this.trigger("escKeyPressed");
                    }
                } else if (event.keyCode === 13) { // ENTER key
                    this.onEnterKeyPress(event, datum);

                    event.preventDefault();
                }
            },
            onEnterKeyPress: function (event, datum) {
                var $db = this.$el.closest(".dataBox");
                $db.find('select#box_ECO_DEV_REGION').value = "FOC_NONE";
                $db.find('select#box_ECODEVREGION').value = "FOC_NONE";
                $db.find('select#box_INSTITUTION_TYPE').value = "FOC_NONE";
                $db.find('select#box_COLLEGE_SELECTIVITY').value = "FOC_NONE";
                $db.find('select#box_COLLEGE_STATE').value = "FOC_NONE";
                var that = this;

                this.setQuery($db.find('input#orgName1').val());
                if (this.hasCausedAutoScroll()) {
                    workspace.showFixedHeader()
                        .then(function () {
                            return that.restoreOriginalScroll();
                        })
                        .catch(Util.reportPromiseException);
                }
            },
            onOrgNameFocusIn: function ( /*event*/) { },
            onOrgNameFocusOut: function ( /*event*/) {

                workspace.showFixedHeader()
                    .then(function () {
                        // return this.restoreOriginalScroll();
                    }.bind(this))
                    .then(function () {
                        var str2 = location.host;

                        if (((str2.indexOf("eclds") > -1) || (str2.indexOf("sleds") > -1)) && (this.$orgName[0].previousSibling.value != "")) {

                            $(this.el.nextSibling.nextSibling.nextSibling.children[0].children[1].children[0].children[0].children[1]).prop('selectedIndex', 0);
                            var $db2 = this.$el.closest(".dataBox");

                            $db2.find('select#box_ECO_DEV_REGION').value = "FOC_NONE";
                            $db2.find('select#box_ECODEVREGION').value = "FOC_NONE";
                            $db.find('select#box_INSTITUTION_TYPE').value = "FOC_NONE";
                            $db.find('select#box_COLLEGE_SELECTIVITY').value = "FOC_NONE";
                            $db.find('select#box_COLLEGE_STATE').value = "FOC_NONE";
                            $db2.find('.filter').value = "";

                            that = this;
                            setTimeout(function () {
                                $db2.find('select#box_ECO_DEV_REGION').value = "FOC_NONE";
                                $db2.find('select#box_ECODEVREGION').value = "FOC_NONE";
                                $db.find('select#box_INSTITUTION_TYPE').value = "FOC_NONE";
                                $db.find('select#box_COLLEGE_SELECTIVITY').value = "FOC_NONE";
                                $db.find('select#box_COLLEGE_STATE').value = "FOC_NONE";

                                if (location.hash.indexOf("outStateCollegeStudentEnrollment") > -1) {
                                    $(this.el.nextSibling.nextSibling.nextSibling.children[1].children[1].children[0].children[0].children[1]).prop('selectedIndex', 0);
                                    $(this.el.nextSibling.nextSibling.nextSibling.children[2].children[1].children[0].children[0].children[1]).prop('selectedIndex', 0);
                                }
                            }, 1000);
                        } else if ($.trim(this.$orgName.val()) === "" && ((location.hash.indexOf("parentAware") < 1) && (location.hash.indexOf("scholarships") < 1) && (str2.indexOf("sleds") < 1))) {
                            this.restoreQuery();
                        }
                    }.bind(this));
            },
            onBeforeHamburgerDrawerOpen: function () {
                this.restoreQuery();
            },
            onMapButtonClicked: function () {
                Backbone.trigger("map:request", {
                    requestingDataBoxView: this.options.parentView.dataBoxView
                });
            },
            onResetButtonClicked: function () {
                this.setQuery("Statewide");
                Backbone.trigger("map:orgReset", {
                    requestingDataBoxView: this.options.parentView.dataBoxView
                });
            }
        });

        /**
         * @class module:cardboard/filterViews.DemographicCategorySelectorView
         * @augments Views.BaseView
         * @classdesc Renders and manages the demographic category selector UI.
         */
        FilterViews.DemographicCategorySelectorView = FilterViews.BaseFilterView.extend( /** @lends module:cardboard/filterViews.DemographicCategorySelectorView.prototype */ {

            /** declarative events. empty for now
             * @type Object */
            events: {
                "change .ui-checkbox": "onFilterChanged",
                "keydown .ui-checkbox": "onKeyPressed"
            },

            loaded: false,

            categoryParamMatcher: /^(demographics|CATEGORY[1-9]+)/,

            /** el will be passed in to this one, and we'll render into
             * that rather than create our own. */
            initialize: function (options) {
                this.options = options;
                this.isCat = true;

                // Do the right thing -- there might not even be a model
                // attached. What if this report has no filters other
                // than the org finder? I bet you hadn't thought of that. I
                // sure hadn't.
                if (this.model) {
                    if (this.model.isLoaded()) {
                        this.render();
                    } else {
                        this.showProgress(".content");
                    }
                    // bind to model's change event
                    this.listenTo(this.model, "change", this.onModelSync.bind(this));

                    // this one is to support the derivative version produced by the
                    // generic categories list, which is used by growth and
                    // graduation, at least
                    this.listenTo(this.model, "reset", this.onModelSync.bind(this));
                } else {
                    this.render();
                }
            },

            /** is this a multi-select, or a grouped type?
             * @returns {Boolean} */
            isMultiSelect: function () {
                return this.model.isMultiSelect();
            },

            render: function () {
                var currentReportName = window.workspace.getCurrentPluginName(),
                    alreadyDone = (this.renderedFor === currentReportName);

                this.unmaskForLoading();
                if (alreadyDone) {
                    //return;
                }

                if (this.model) {
                    // actually create the options and attach them to the DOM
                    var dataView = this.model.toJSON();

                    if (this.isMultiSelect()) {
                        this.$el.html(demoCatsMultiTemplate.render(dataView));
                    } else {
                        this.$el.html(demoCatsTemplate.render(dataView));
                    }

                    // create the jqMobile widgets
                    this.$el.trigger("create");
                } else {
                    // we want an empty demo cats box.
                    // this may be redundant with a similar thing in createDemoCatsView()
                    this.$el.html("");
                }

                // let people know we're done here
                this.renderedFor = currentReportName;
                this.loaded = true;
            },

            /** makes the buttons reflect the selection state passed in */
            updateSelections: function (selectionStateToRestore) {
                // NOTE: in some reports, these are the named group "CATEGORY1"; in
                // others, "demographics". This should get fixed once we have a
                // clean backend.
                var categoryParamName = "categories",
                    paramValue = selectionStateToRestore[categoryParamName],
                    values;

                // if there's no categories parameter, just bail
                if (!paramValue) {
                    return;
                }

                // make 'em all unchecked to begin with
                this.$el.find("input[type='checkbox']")
                    .prop("checked", false)
                    .checkboxradio("refresh");

                if (_.isString(paramValue)) {
                    values = paramValue.split("|");
                } else if (_.isArray(paramValue)) {
                    values = paramValue;
                } else {
                    return;
                }

                // now, go over them all and enabled the ones that were asked for
                _(values).each(function (value) {
                    // check only the ones that should be checked
                    this.$el.find("input[name='" + categoryParamName + "'][value='" + value + "']")
                        .prop("checked", true)
                        .checkboxradio("refresh");
                }, this);
                //provide a dummy event to lookup any duplicate checked items
                this.limitSelections({
                    target: this.$el.find("input:checked")[0]
                });
            },

            //
            // EVENT HANDLERS
            //

            // handles a DOM event
            onFilterChanged: function (event /*, info*/) {
                this.limitSelections(event);
                // fire a backbone event
                this.trigger("filterChanged", {
                    optionElement: event.target
                });
            },
            /** enforce limits on single-select toggles -- this gets us the behavior
             * of radio buttons without the fuss, allowing every toggle's underlying
             * form element to be a checkbox.
             * @param {Event} event the originating event, passed here from the event handler */
            limitSelections: function (event) {
                var self = this,
                    $clickedOne = event ? $(event.target) : null,
                    $controlGroups;

                if ($clickedOne) {
                    $controlGroups = $clickedOne.closest("[data-role=controlgroup]");
                } else {
                    $controlGroups = this.$el.find("[data-role=controlgroup]");
                }

                $controlGroups.each(function () {
                    var $controlGroup = $(this);
                    var $allCheckedOnes = $controlGroup.find("input:checked");

                    if (self.isMultiSelect() === "false") {
                        // if there's only one or none checked, we're good.
                        if ($allCheckedOnes.length < 2) {
                            return;
                        }

                        // however, if there's more than one checked, this is
                        // now unacceptable. Uncheck the old one!
                        $allCheckedOnes.not($clickedOne).prop("checked", false).checkboxradio("refresh");
                    } else {
                        var $allUncheckedOnes = $controlGroup.find("input[type=checkbox]").not(":checked");
                        var demoCatsOptions = PluginHelper.getCurrentPluginDemoCatsOptions();
                        var maxSelections = demoCatsOptions.maxSelections ||
                            parseInt($controlGroup.attr("data-selectionlimit"), 10);

                        if (maxSelections === 1) {
                            if ($clickedOne) {
                                $allCheckedOnes.not($clickedOne).prop("checked", false).checkboxradio("refresh");
                            }
                        } else if (maxSelections > 1) {
                            if ($allCheckedOnes.length >= maxSelections) {
                                $allUncheckedOnes.checkboxradio("disable");
                            } else {
                                $allUncheckedOnes.checkboxradio("enable");
                            }
                        }
                    }
                });
            },
            onModelSync: function () {
                this.render();
            },
            onKeyPressed: function (evt) {
                if (evt.keyCode === 13) { // ENTER key
                    $(evt.target).trigger('click');
                }
            }
        });

        return FilterViews;
    });
